import * as React from 'react'
import { useContext, useMemo, useState } from 'react'
import { CompoundButton, Text } from '@fluentui/react-components'
import * as UUID from 'uuid'
import AlertDialog from '../../../components/Dialog/AlertDialog'
import { LegalAidCertificateInvoiceProps } from './Invoices'
import { useFormikContext } from 'formik'
import { LegalAidCertificateDto } from './LegalAidCertificateForm'
import { useThemeClassName_unstable } from '@fluentui/react-shared-contexts'
import { formatCurrencyDecimals } from '../../../helpers/numberHelper'
import { InvoiceDto, LineItemType } from '../../../config/finance/invoiceConfig'
import usePersistLegalAidCertificateInvoice, { PrefillLineItems } from '../../../hooks/finance/invoice/usePersistLegalAidCertificateInvoice'
import GlobalContext, { GlobalContextType } from '../../../context/GlobalContext'
import useCurrency from '../../../hooks/useCurrency'
import { bundleIcon, DocumentQueueAddFilled, DocumentQueueAddRegular } from '@fluentui/react-icons'
import useEntity from '../../../hooks/useEntity'
import { QueueStatus } from '../../../hooks/queue/queueTypes'
import useSubmitInvoiceDocumentGeneration from '../../../hooks/finance/invoice/useSubmitInvoiceDocumentGeneration'
import { DocumentGenerationType } from '../../../hooks/document/documentGenerationTypes'
import { useTranslation } from 'react-i18next'
import useAxios from '../../../hooks/useAxios'
import { MainDto } from '../../../config/filePartyConfig'

const DocumentQueueIcon = bundleIcon(DocumentQueueAddFilled, DocumentQueueAddRegular)

export default function CreateSettlementInvoiceButton(
    props: LegalAidCertificateInvoiceProps & { canCreateInvoice: boolean; pending: boolean; setPending: (pending: boolean) => void },
) {
    const {
        settings: { legalAidCertificate: settings },
    } = useContext<GlobalContextType>(GlobalContext)
    const { values, setFieldValue, setFieldError } = useFormikContext<LegalAidCertificateDto>()
    const { t: translate } = useTranslation()
    const axios = useAxios()
    const requestId = useMemo(() => UUID.v4(), [props.selected?.id, values.settlementInvoiceId])
    const { currency } = useCurrency(values.currency)
    const persist = usePersistLegalAidCertificateInvoice(props.file, props.parties)
    const generatePdf = useSubmitInvoiceDocumentGeneration(DocumentGenerationType.Invoice)

    const [contributionInvoice] = useEntity<InvoiceDto>((id: string) => ({ url: `/finance/invoice/${id}` }), values.contributionInvoiceId)
    const [isAlertOpen, setAlertOpen] = useState(false)

    const themeClassName = useThemeClassName_unstable()

    // A settlement invoice without an own contribution invoice is allowed (e.g. criminal law),
    // but only when no own contribution amount has been entered.
    const contributionRequired = useMemo<boolean>(() => Number(values.contribution) > 0, [values.contribution])
    const canCreateSettlementInvoice = useMemo<boolean>(
        () =>
            !values.settlementInvoiceId &&
            props.canCreateInvoice &&
            !props.pending &&
            (!contributionRequired || (!!values.contributionInvoiceId && contributionInvoice.loaded)),
        [
            props.canCreateInvoice,
            props.pending,
            values.settlementInvoiceId,
            values.contributionInvoiceId,
            contributionInvoice.loaded,
            contributionRequired,
        ],
    )

    const createSettlementInvoice = async (prefillLineItems?: PrefillLineItems) => {
        props.setPending(true)
        try {
            // Persist the certificate first: the legal aid board (the debtor of the settlement) only becomes
            // a party of the file when the certificate is saved, and an unsaved form has no stored
            // certificate to link the invoice to.
            const saved = await props.saveCertificate(values, setFieldError)
            if (!saved) {
                props.setPending(false)
                setAlertOpen(false)
                return
            }
            // Select the stored certificate and refresh the lists only once the flow is done: the refresh
            // remounts the Formik subtree.
            const finish = () => {
                props.setSelected({ ...props.selected, id: saved.certificateId, isNew: false, isDirty: false })
                props.reset?.()
                props.setPending(false)
            }
            // Fetch the parties again, as the (possibly just created) debtor may not be in the loaded list yet.
            const parties = await axios.get<MainDto[]>(`/file/${values.fileId}/party`).then(response => response.data)
            const request = persist(
                requestId,
                {
                    templateId: settings.settlementInvoice.templateId,
                    clientPivotId: values.clientPivotId,
                    debtorPivotId: saved.legalAidBoardPivotId,
                    debtor: parties.find(party => party.pivotId === saved.legalAidBoardPivotId),
                    currency: values.currency,
                    recipientReference: values.reference,
                },
                data => {
                    // Link the invoice to the certificate as soon as it exists, independent of the PDF
                    // generation, through an explicit save: submitting the Formik state right after a
                    // setFieldValue races the batched state update and can persist stale values.
                    props
                        .saveCertificate(
                            { ...values, legalAidBoardPivotId: saved.legalAidBoardPivotId, settlementInvoiceId: data.invoiceId },
                            setFieldError,
                            { certificateId: saved.certificateId },
                        )
                        .then(() => setFieldValue('settlementInvoiceId', data.invoiceId))
                    generatePdf(data, {
                        [QueueStatus.Dispatch]: () => {
                            setAlertOpen(false)
                        },
                        [QueueStatus.Finished]: finish,
                        [QueueStatus.Cancelled]: finish,
                        [QueueStatus.Failed]: finish,
                    })
                },
                {
                    [settings.settlementInvoice.feeTemplateId]: formatCurrencyDecimals(
                        Number(values.baseFee) + Number(values.travelFee) + Number(values.fixedCompensation),
                        currency,
                        'en',
                        'en',
                        false,
                    ),
                    [settings.settlementInvoice.travelTemplateId]: values.travelExpenses,
                },
                prefillLineItems,
            )
            if (request) {
                request.catch(() => {
                    setAlertOpen(false)
                    finish()
                })
            } else {
                setAlertOpen(false)
                finish()
            }
        } catch {
            props.setPending(false)
            setAlertOpen(false)
        }
    }

    return (
        <>
            <CompoundButton
                disabled={!canCreateSettlementInvoice}
                secondaryContent={
                    contributionRequired && !values.contributionInvoiceId
                        ? translate('legalaidcertificate.settlement.contribution.required')
                        : 'Verwerk vergoeding RvR'
                }
                appearance="subtle"
                icon={<DocumentQueueIcon />}
                onClick={() => {
                    if (!values.contributionInvoiceId) {
                        // Without an own contribution invoice there is nothing to offset; create the settlement directly.
                        createSettlementInvoice()
                    } else if (contributionInvoice.data?.credit?.length > 0) {
                        setAlertOpen(true)
                    } else {
                        createSettlementInvoice({
                            [settings.settlementInvoice.contributionTemplateId]: [
                                {
                                    type: LineItemType.Invoice,
                                    entityId: values.contributionInvoiceId,
                                },
                            ],
                        })
                    }
                }}
            >
                Eindafrekening RvR
            </CompoundButton>
            <AlertDialog
                className={themeClassName}
                content={
                    <>
                        <Text block>
                            De declaratie voor de eigen bijdrage is al verrekend in declaratie(s)&nbsp;
                            <Text weight="semibold">{contributionInvoice.data?.credit?.map(item => item.number).join(', ')}</Text>.
                        </Text>
                        <Text block>Wilt u doorgaan zonder verrekening van de eigen bijdrage?</Text>
                    </>
                }
                onConfirm={() => createSettlementInvoice()}
                setOpen={setAlertOpen}
                open={isAlertOpen}
            />
        </>
    )
}
